import { ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";

import { cn } from "@src/shared/lib/utils";

export interface AccordionProps {
  /** Content for the accordion header (e.g. title, subtitle, badge). */
  header: ReactNode;
  /** Content shown when the accordion is expanded. */
  children: ReactNode;
  /** Whether the accordion starts expanded. Defaults to false. */
  initialOpen?: boolean;
  className?: string;
}

/**
 * Lightweight accordion with a chevron that rotates on open. Used inline
 * within cards (unlike the full shadcn `Accordion` primitive which renders a
 * separator line).
 */
export const Accordion = ({
  header,
  children,
  initialOpen = false,
  className,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={cn("w-full", className)}>
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
          "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        <ChevronRight
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-90",
          )}
        />
        <div className="flex-1">{header}</div>
      </button>
      {isOpen ? <div className="pl-6 pt-2">{children}</div> : null}
    </div>
  );
};
